import { useState, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { api } from "@shared/routes";
import { CORES } from "@/lib/constants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  X,
  Download,
  Loader2,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface VeiculoRow {
  tipo: string;
  placa: string;
  numeroNota: string;
  chassi: string;
  cor: string;
  marca: string;
  modelo: string;
  anoFabricacao?: number;
  anoModelo?: number;
  numeroMotor?: string;
  renavam?: string;
}

interface ImportResult {
  criados: number;
  ignorados: number;
  erros: { linha: number; razao: string }[];
}

// ---------------------------------------------------------------------------
// Column aliases (case-insensitive)
// ---------------------------------------------------------------------------
const COLUMN_ALIASES: Record<keyof VeiculoRow, string[]> = {
  tipo: ["tipo", "tipo_veiculo", "tipo veiculo", "tipoveiculo"],
  placa: ["placa"],
  numeroNota: ["numeronota", "numero_nota", "número nota", "numero nota", "nota", "nf", "nota fiscal"],
  chassi: ["chassi", "chassis", "vin"],
  cor: ["cor", "color", "colour"],
  marca: ["marca", "fabricante", "brand"],
  modelo: ["modelo", "model"],
  anoFabricacao: ["anofabricacao", "ano_fabricacao", "ano fabricacao", "ano fabricação", "anofab", "fab"],
  anoModelo: ["anomodelo", "ano_modelo", "ano modelo", "anomod", "mod"],
  numeroMotor: ["numeromotor", "numero_motor", "número motor", "numero motor", "motor"],
  renavam: ["renavam"],
};

// Tipo aliases for flexible input
const TIPO_ALIASES: Record<string, string> = {
  auto_leve: "auto_leve",
  autoleve: "auto_leve",
  "auto leve": "auto_leve",
  carro: "auto_leve",
  leve: "auto_leve",
  automovel: "auto_leve",
  automóvel: "auto_leve",
  moto: "moto",
  motocicleta: "moto",
  caminhao: "caminhao",
  caminhão: "caminhao",
  truck: "caminhao",
  onibus: "onibus",
  ônibus: "onibus",
  bus: "onibus",
};

function normalizeKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

function mapHeader(header: string): keyof VeiculoRow | null {
  const norm = normalizeKey(header);
  for (const [field, aliases] of Object.entries(COLUMN_ALIASES)) {
    if (aliases.includes(norm)) return field as keyof VeiculoRow;
  }
  return null;
}

function normalizeTipo(raw: string): string | null {
  return TIPO_ALIASES[normalizeKey(raw)] ?? null;
}

// Matches free-text cor input ("amarela", "01 - Amarelo", "AMARELO"...) to the
// canonical "codigo - Nome" format used by the cor dropdown (see CORES in constants.ts).
function normalizeCor(raw: string): string | null {
  const norm = normalizeKey(raw).replace(/^\d+\s*-\s*/, "");
  const altGender = norm.endsWith("a") ? norm.slice(0, -1) + "o" : norm.endsWith("o") ? norm.slice(0, -1) + "a" : norm;
  const match = CORES.find((c) => {
    const nome = normalizeKey(c.nome);
    return nome === norm || nome === altGender;
  });
  return match ? `${match.codigo} - ${match.nome}` : null;
}

// ---------------------------------------------------------------------------
// CSV parser
// ---------------------------------------------------------------------------
function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { cell += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === "," || ch === ";") { row.push(cell); cell = ""; }
      else if (ch === "\n" || (ch === "\r" && text[i + 1] === "\n")) {
        if (ch === "\r") i++;
        row.push(cell); cell = "";
        if (row.some(c => c.trim() !== "")) rows.push(row);
        row = [];
      } else { cell += ch; }
    }
  }
  if (cell !== "" || row.length > 0) { row.push(cell); if (row.some(c => c.trim() !== "")) rows.push(row); }
  return rows;
}

// ---------------------------------------------------------------------------
// Parse file → VeiculoRow[]
// ---------------------------------------------------------------------------
async function parseFile(file: File): Promise<{ rows: VeiculoRow[]; warnings: string[] }> {
  const warnings: string[] = [];
  let rawRows: string[][];

  if (file.name.endsWith(".csv") || file.type === "text/csv") {
    const text = await file.text();
    rawRows = parseCSV(text);
  } else {
    const buffer = await file.arrayBuffer();
    const wb = XLSX.read(buffer, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const data: string[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" }) as string[][];
    rawRows = data.map(r => r.map(c => String(c ?? "")));
  }

  if (rawRows.length < 2) throw new Error("Arquivo vazio ou sem dados além do cabeçalho.");

  const headerRow = rawRows[0];
  const fieldMap: Record<number, keyof VeiculoRow> = {};
  for (let col = 0; col < headerRow.length; col++) {
    const field = mapHeader(headerRow[col]);
    if (field) fieldMap[col] = field;
  }

  const required: (keyof VeiculoRow)[] = ["tipo", "placa", "numeroNota", "chassi", "cor", "marca", "modelo"];
  for (const req of required) {
    if (!Object.values(fieldMap).includes(req)) {
      throw new Error(`Coluna obrigatória não encontrada: "${req}". Verifique o cabeçalho do arquivo.`);
    }
  }

  const rows: VeiculoRow[] = [];

  for (let i = 1; i < rawRows.length; i++) {
    const dataRow = rawRows[i];
    if (dataRow.every(c => c.trim() === "")) continue;

    const obj: Partial<VeiculoRow> = {};
    for (const [colStr, field] of Object.entries(fieldMap)) {
      const col = Number(colStr);
      const val = dataRow[col]?.trim() ?? "";
      if (val === "") continue;
      if (field === "anoFabricacao" || field === "anoModelo") {
        const num = parseInt(val, 10);
        if (!isNaN(num)) (obj as any)[field] = num;
      } else {
        (obj as any)[field] = val;
      }
    }

    const linha = i + 1;

    if (!obj.tipo) { warnings.push(`Linha ${linha}: Tipo vazio — ignorada`); continue; }
    const tipoNorm = normalizeTipo(obj.tipo);
    if (!tipoNorm) { warnings.push(`Linha ${linha}: Tipo inválido "${obj.tipo}" — ignorada`); continue; }
    obj.tipo = tipoNorm;

    if (!obj.placa) { warnings.push(`Linha ${linha}: Placa vazia — ignorada`); continue; }
    if (!obj.numeroNota) { warnings.push(`Linha ${linha}: Número da nota vazio — ignorada`); continue; }
    if (!obj.chassi) { warnings.push(`Linha ${linha}: Chassi vazio — ignorada`); continue; }
    if (!obj.cor) { warnings.push(`Linha ${linha}: Cor vazia — ignorada`); continue; }
    const corNorm = normalizeCor(obj.cor);
    if (!corNorm) { warnings.push(`Linha ${linha}: Cor inválida "${obj.cor}" — ignorada`); continue; }
    obj.cor = corNorm;
    if (!obj.marca) { warnings.push(`Linha ${linha}: Marca vazia — ignorada`); continue; }
    if (!obj.modelo) { warnings.push(`Linha ${linha}: Modelo vazio — ignorada`); continue; }

    rows.push(obj as VeiculoRow);
  }

  if (rows.length === 0) throw new Error("Nenhuma linha válida encontrada no arquivo.");
  return { rows, warnings };
}

// ---------------------------------------------------------------------------
// Template download
// ---------------------------------------------------------------------------
function downloadTemplate() {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([
    ["tipo", "placa", "numeroNota", "chassi", "cor", "marca", "modelo", "anoFabricacao", "anoModelo", "numeroMotor", "renavam"],
    ["auto_leve", "ABC1D23", "000000001", "9BWZZZ377VT004251", "01 - Amarelo", "HONDA", "Civic 1.5 Turbo", "2022", "2023", "K20C4", "00123456789"],
    ["moto", "XYZ9B78", "000000002", "9C2JC4114MR000001", "04 - Branca", "YAMAHA", "MT-07", "2021", "2021", "", ""],
  ]);
  ws["!cols"] = [
    { wch: 12 }, { wch: 10 }, { wch: 14 }, { wch: 20 }, { wch: 20 },
    { wch: 12 }, { wch: 20 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 14 },
  ];
  XLSX.utils.book_append_sheet(wb, ws, "Veículos");
  XLSX.writeFile(wb, "modelo_importacao_veiculos.xlsx");
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface Props {
  open: boolean;
  onClose: () => void;
  isVistoriadoraAdmin?: boolean;
}

type Step = "upload" | "preview" | "result";

const TIPO_LABELS: Record<string, string> = {
  auto_leve: "Auto Leve",
  moto: "Moto",
  caminhao: "Caminhão",
  onibus: "Ônibus",
};

export function ImportarVeiculosDialog({ open, onClose, isVistoriadoraAdmin }: Props) {
  const { session } = useAuth();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>("upload");
  const [dragging, setDragging] = useState(false);
  const [rows, setRows] = useState<VeiculoRow[]>([]);
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [result, setResult] = useState<ImportResult | null>(null);
  const [selectedParceiroId, setSelectedParceiroId] = useState<string>("");
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: parceiros } = useQuery({
    queryKey: [api.veiculos.parceiros.path],
    queryFn: async () => {
      const res = await fetch(api.veiculos.parceiros.path, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) return [];
      return res.json() as Promise<{ id: string; razaoSocial: string; nomeFantasia?: string }[]>;
    },
    enabled: !!isVistoriadoraAdmin && open,
  });

  const reset = () => {
    setStep("upload");
    setRows([]);
    setParseWarnings([]);
    setParseError(null);
    setFileName("");
    setResult(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFile = useCallback(async (file: File) => {
    setParseError(null);
    setFileName(file.name);
    try {
      const { rows: parsed, warnings } = await parseFile(file);
      setRows(parsed);
      setParseWarnings(warnings);
      setStep("preview");
    } catch (e: any) {
      setParseError(e.message ?? "Erro ao processar arquivo.");
    }
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const importMutation = useMutation({
    mutationFn: async (veiculoRows: VeiculoRow[]) => {
      if (isVistoriadoraAdmin && !selectedParceiroId) {
        throw new Error("Selecione um parceiro de negócio antes de importar.");
      }
      const res = await fetch(api.veiculos.createBatch.path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          veiculos: veiculoRows,
          parceiroId: isVistoriadoraAdmin ? selectedParceiroId : undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao importar");
      }
      return res.json() as Promise<ImportResult>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [api.veiculos.list.path] });
      setResult(data);
      setStep("result");
      if (data.criados > 0) {
        toast({ title: `${data.criados} veículo(s) importado(s) com sucesso!` });
      }
    },
    onError: (err: Error) => {
      toast({ title: "Erro na importação", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
            Importar Veículos em Lote
          </DialogTitle>
        </DialogHeader>

        {/* ── STEP: UPLOAD ── */}
        {step === "upload" && (
          <div className="space-y-4 flex-1">
            {/* Parceiro selector (vistoriadora only) */}
            {isVistoriadoraAdmin && (
              <div className="rounded-lg border bg-slate-50 px-4 py-3 space-y-2">
                <Label>Parceiro de Negócio *</Label>
                <Select value={selectedParceiroId} onValueChange={setSelectedParceiroId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o parceiro" />
                  </SelectTrigger>
                  <SelectContent>
                    {(parceiros ?? []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nomeFantasia || p.razaoSocial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Todos os veículos serão importados para este parceiro</p>
              </div>
            )}

            {/* Template download */}
            <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Modelo de planilha</p>
                <p className="text-xs text-muted-foreground">Baixe o modelo com as colunas corretas antes de importar</p>
              </div>
              <Button size="sm" variant="outline" onClick={downloadTemplate}>
                <Download className="w-4 h-4 mr-2" />
                Baixar modelo
              </Button>
            </div>

            {/* Columns info */}
            <div className="rounded-lg border px-4 py-3 text-sm space-y-1">
              <p className="font-medium text-muted-foreground mb-2">Colunas aceitas:</p>
              <div className="grid grid-cols-2 gap-x-8 gap-y-0.5 text-xs">
                <span><strong>tipo</strong> — obrigatório (auto_leve, moto, caminhao, onibus)</span>
                <span><strong>placa</strong> — obrigatório</span>
                <span><strong>numeroNota</strong> — obrigatório</span>
                <span><strong>chassi</strong> — obrigatório</span>
                <span><strong>cor</strong> — obrigatório</span>
                <span><strong>marca</strong> — obrigatório</span>
                <span><strong>modelo</strong> — obrigatório</span>
                <span>anoFabricacao — opcional</span>
                <span>anoModelo — opcional</span>
                <span>numeroMotor — opcional</span>
                <span>renavam — opcional</span>
              </div>
            </div>

            {/* Drop zone */}
            <div
              className={`rounded-xl border-2 border-dashed transition-colors cursor-pointer flex flex-col items-center justify-center gap-3 py-12 ${
                dragging ? "border-primary bg-primary/5" : "border-muted-foreground/30 hover:border-primary/50"
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="w-10 h-10 text-muted-foreground" />
              <div className="text-center">
                <p className="font-medium">Arraste o arquivo aqui ou clique para selecionar</p>
                <p className="text-xs text-muted-foreground mt-1">CSV ou XLSX (Excel)</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
              />
            </div>

            {parseError && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{parseError}</span>
              </div>
            )}
          </div>
        )}

        {/* ── STEP: PREVIEW ── */}
        {step === "preview" && (
          <div className="flex flex-col flex-1 min-h-0 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">{fileName}</span>
                <Badge variant="secondary">{rows.length} registro(s)</Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={reset}>
                <X className="w-4 h-4 mr-1" /> Trocar arquivo
              </Button>
            </div>

            {parseWarnings.length > 0 && (
              <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-800 space-y-0.5">
                <p className="font-medium">Avisos durante leitura:</p>
                {parseWarnings.map((w, i) => <p key={i}>{w}</p>)}
              </div>
            )}

            {isVistoriadoraAdmin && selectedParceiroId && (
              <div className="rounded-lg border bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Parceiro selecionado: <strong>{parceiros?.find(p => p.id === selectedParceiroId)?.nomeFantasia || parceiros?.find(p => p.id === selectedParceiroId)?.razaoSocial}</strong>
              </div>
            )}

            {/* Preview table */}
            <div className="flex-1 overflow-auto rounded-lg border">
              <table className="w-full text-xs">
                <thead className="bg-muted/60 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">#</th>
                    <th className="px-3 py-2 text-left font-medium">Tipo</th>
                    <th className="px-3 py-2 text-left font-medium">Placa</th>
                    <th className="px-3 py-2 text-left font-medium">Marca / Modelo</th>
                    <th className="px-3 py-2 text-left font-medium">Chassi</th>
                    <th className="px-3 py-2 text-left font-medium">Cor</th>
                    <th className="px-3 py-2 text-left font-medium">Nota</th>
                    <th className="px-3 py-2 text-left font-medium">Ano</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-t hover:bg-muted/20">
                      <td className="px-3 py-2 text-muted-foreground">{i + 2}</td>
                      <td className="px-3 py-2">{TIPO_LABELS[row.tipo] ?? row.tipo}</td>
                      <td className="px-3 py-2 font-mono font-medium">{row.placa}</td>
                      <td className="px-3 py-2 max-w-[160px] truncate">{row.marca} {row.modelo}</td>
                      <td className="px-3 py-2 font-mono text-muted-foreground text-[10px]">{row.chassi}</td>
                      <td className="px-3 py-2 max-w-[100px] truncate text-muted-foreground">{row.cor}</td>
                      <td className="px-3 py-2 text-muted-foreground">{row.numeroNota}</td>
                      <td className="px-3 py-2 text-muted-foreground">{row.anoFabricacao ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={reset}>Cancelar</Button>
              <Button
                onClick={() => importMutation.mutate(rows)}
                disabled={importMutation.isPending || (isVistoriadoraAdmin && !selectedParceiroId)}
              >
                {importMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Importar {rows.length} veículo(s)
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP: RESULT ── */}
        {step === "result" && result && (
          <div className="flex-1 space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border bg-green-50 border-green-200 px-4 py-3 text-center">
                <p className="text-2xl font-bold text-green-700">{result.criados}</p>
                <p className="text-xs text-green-600 mt-0.5">Criados</p>
              </div>
              <div className="rounded-lg border bg-yellow-50 border-yellow-200 px-4 py-3 text-center">
                <p className="text-2xl font-bold text-yellow-700">{result.ignorados}</p>
                <p className="text-xs text-yellow-600 mt-0.5">Ignorados (placa duplicada)</p>
              </div>
              <div className="rounded-lg border bg-red-50 border-red-200 px-4 py-3 text-center">
                <p className="text-2xl font-bold text-red-700">{result.erros.length}</p>
                <p className="text-xs text-red-600 mt-0.5">Erros</p>
              </div>
            </div>

            {result.erros.length > 0 && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
                <p className="text-sm font-medium text-destructive mb-2 flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4" />
                  Linhas com erro:
                </p>
                <div className="space-y-1 max-h-40 overflow-auto text-xs">
                  {result.erros.map((e, i) => (
                    <p key={i} className="text-destructive">Linha {e.linha}: {e.razao}</p>
                  ))}
                </div>
              </div>
            )}

            {result.criados > 0 && result.erros.length === 0 && (
              <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-700">
                <CheckCircle2 className="w-5 h-5" />
                Importação concluída com sucesso!
              </div>
            )}

            <div className="flex justify-end gap-2">
              {result.erros.length > 0 && (
                <Button variant="outline" onClick={reset}>Tentar novamente</Button>
              )}
              <Button onClick={handleClose}>Fechar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
